import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useFetchDataId } from 'services/queries';
import { useParams } from "react-router-dom";
import { useQueryClient } from '@tanstack/react-query';
import SummaryHeader from '../sections/common/SummaryHeader';
import CustomsValueSection from '../sections/common/CustomsValueSection';
import CustomsTaxesSection from '../sections/common/CustomsTaxesSection';
import ClientEntryInvoiceSection from '../sections/common/ClientEntryInvoiceSection';
import CommercialAgreementSection from '../sections/common/CommercialAgreementSection';
import TaxCreditsSection from '../sections/common/TaxCreditsSection';
import ProductTable from '../sections/common/ProductTable';
import NoTaxCalculationPrompt from '../components/NoTaxCalculationPrompt';

/**
 * AccountAndOrderView - Strategy component for Account and Order (Third Parties Import) order type
 * @param {Object} props - Component props
 * @param {Object} props.order - Order data
 */
const AccountAndOrderView = ({ order }) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { data: taxCalculation, error, isLoading } = useFetchDataId(`tax/order-tax-calculation/latest`, id, true);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Handle errors
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading tax calculation data. Please try again later.
      </Alert>
    );
  }

  // Check if tax calculation is empty (no tax calculation exists)
  if (taxCalculation && Object.keys(taxCalculation).length === 0) {
    return (
      <NoTaxCalculationPrompt 
        order={order} 
        onCalculationComplete={() => {
          // Invalidate the query to refetch the data
          queryClient.invalidateQueries(['tax/order-tax-calculation/latest', id]);
        }}
      />
    );
  }

  // Combine order and tax calculation data
  const combinedData = {
    ...order,
    latest_tax_calculation: taxCalculation
  };

  const exchangeRate = taxCalculation?.exchange_rate_dollar_to_real || 1; // Get exchange rate with fallback

  console.log('AccountAndOrderView - Combined Data:', combinedData);
  console.log('AccountAndOrderView - Exchange Rate:', exchangeRate);

  return (
    <Box p={2}>
      {/* Summary Header - shows calculation summary */}
      <Box className="print-section page-break-inside-avoid summary-grid">
        <SummaryHeader taxCalculation={taxCalculation} order={order} />
      </Box>

      {/* Common sections for Third Parties Import */}
      <Box className="print-section page-break-inside-avoid">
        <CustomsValueSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <CustomsTaxesSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <ClientEntryInvoiceSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <CommercialAgreementSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <TaxCreditsSection order={combinedData} />
      </Box>
      <Box className="print-section page-break-inside-avoid">
        <ProductTable order={combinedData} exchangeRate={exchangeRate} />
      </Box>
      
      {/* Note: Additional sections specific to Third Parties Import can be added here
          as requirements are clarified */}
    </Box>
  );
};

export default AccountAndOrderView;